import { motion } from "framer-motion";
import { Ear, Flame, Gem } from "lucide-react";

const IMG_ENTRANCE = "https://customer-assets-agu9un31.emergentagent.net/job_2e5c0fe7-21bd-4845-ae98-db1e0d67887b/artifacts/91789042a3e0e8eb_barberia.webp";

const PILLARS = [
  {
    icon: Ear,
    title: "Ascolto e Consulenza",
    text: "Non esiste un taglio universale. Ogni viso, ogni tipo di capello e ogni barba raccontano una storia. Prima di toccare le forbici voglio capire chi sei, qual è il tuo stile e cosa desideri.",
  },
  {
    icon: Flame,
    title: "Tradizione e Innovazione",
    text: "Amo i rituali classici della vecchia scuola — come la rasatura tradizionale con panno caldo e freddo — ma non smetto mai di aggiornarmi sulle ultime tendenze e sulle tecniche di sfumatura moderne.",
  },
  {
    icon: Gem,
    title: "Qualità senza compromessi",
    text: "Utilizzo solo prodotti di prima scelta per la salute dei tuoi capelli. Ogni strumento è sterilizzato e curato nei minimi dettagli.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export function Philosophy() {
  return (
    <section id="filosofia" className="relative py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div {...fadeUp} className="lg:col-span-5 relative">
            <div className="lg:sticky lg:top-28">
              <div className="relative overflow-hidden rounded-lg border border-white/10 aspect-[3/4]">
                <img
                  src={IMG_ENTRANCE}
                  alt="Ingresso di Modico Barberia con muro in pietra e nicchia arancione"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
                <p className="absolute bottom-5 left-5 text-[10px] font-mono uppercase tracking-[0.25em] text-sand">
                  Via Cardinale Cicada 5 — Atri
                </p>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-3">
                {[
                  { v: "60", u: "min", l: "per ogni cliente" },
                  { v: "1", u: "poltrona", l: "alla volta" },
                  { v: "100%", u: "", l: "artigianale" },
                ].map((s) => (
                  <div key={s.l} className="bg-espresso border border-white/[0.08] rounded-md p-4 text-center">
                    <p className="font-serif text-3xl text-ember">
                      {s.v}
                      {s.u && <span className="text-sm text-sand ml-1">{s.u}</span>}
                    </p>
                    <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-stone">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <motion.p {...fadeUp} className="text-xs font-mono tracking-[0.3em] uppercase text-ember">
              Il mio mestiere, la mia passione
            </motion.p>
            <motion.h2 {...fadeUp} className="mt-4 font-serif font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl text-ivory">
              Un'arte antica, un rito fatto di <span className="italic text-ember">tempo, cura e ascolto.</span>
            </motion.h2>

            <motion.div {...fadeUp} className="mt-8 space-y-5 text-base sm:text-lg text-sand leading-relaxed">
              <p data-testid="philosophy-daniele-quote">
                Entrare nella mia bottega non significa solo sedersi su una sedia e dare un taglio ai capelli.
                Sono <span className="text-ivory font-medium">Daniele De Sanctis</span>, titolare di Modico Barberia,
                e dedico la mia vita a valorizzare lo stile e l'immagine di ogni uomo che varca la mia porta.
                La mia non è solo una professione: è una passione viscerale che porto dentro da sempre.
                È l'amore per il dettaglio, per il profumo del panno caldo, per il suono delle forbici che si
                muovono a ritmo e per la precisione millimetrica di un rasoio a mano libera.
              </p>
              <p>
                Il mio obiettivo è farti uscire dalla bottega con il sorriso e la sicurezza di chi si sente
                al proprio meglio. Per riuscirci, seguo una filosofia molto semplice.
              </p>
            </motion.div>

            <div className="mt-10 space-y-4">
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex gap-5 bg-espresso border border-white/[0.08] hover:border-ember/40 rounded-md p-6 transition-colors duration-300"
                >
                  <div className="shrink-0 w-11 h-11 rounded-sm bg-ember/10 border border-ember/25 flex items-center justify-center text-ember group-hover:bg-ember group-hover:text-white transition-colors duration-300">
                    <p.icon size={19} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-ivory">{p.title}</h3>
                    <p className="mt-2 text-sm sm:text-base text-sand leading-relaxed">{p.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div {...fadeUp} className="mt-12 border-l-2 border-ember pl-6 sm:pl-8">
              <h3 className="font-serif italic text-2xl sm:text-3xl text-ivory">La mia bottega: la tua pausa dal mondo</h3>
              <p className="mt-4 text-base sm:text-lg text-sand leading-relaxed">
                Per me, la poltrona del barbiere è un luogo sacro. È uno spazio dove staccare la spina dalla
                frenesia quotidiana, bere un buon caffè e concedersi del tempo per sé stessi. Metto il cuore in
                ogni singola sfumatura, in ogni linea di barba e in ogni finish, perché la mia soddisfazione più
                grande è vedere un cliente specchiarsi e ritrovarsi nella versione migliore di sé.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
